const ORIGIN = 'https://minimuralnails.com';
const BRAND = 'MINI MURAL';

const PUBLIC_PAGES = {
  '/': {
    title: 'MINI MURAL | Custom Press-On Nails & Nail Design Studio',
    description: 'Design your own press-on nails with MINI MURAL. Choose shapes, colors, patterns and charms, explore nail sets, and find your fit with our size guide.',
  },
  '/shop': {
    title: 'Shop Press-On Nail Designs | MINI MURAL',
    description: 'Explore MINI MURAL press-on nail designs, compare styles and colors, and choose a starting point for your own nail set.',
  },
  '/studio': {
    title: 'Custom Nail Design Studio | MINI MURAL',
    description: 'Create your own press-on nail design. Choose nail shapes and colors, add patterns, hand-painted details, stickers and charms, and review your set.',
  },
  '/inspiration': {
    title: 'Press-On Nail Design Inspiration | MINI MURAL',
    description: 'Explore nail art inspiration from MINI MURAL and find colors, patterns and details to bring into your own press-on nail design.',
  },
  '/custom-nails': {
    title: 'How to Create Custom Press-On Nails | MINI MURAL',
    description: 'Learn how to design a MINI MURAL press-on nail set, choose your sizes, review your design and follow the ordering process.',
  },
  '/size-guide': {
    title: 'Press-On Nail Size Guide | MINI MURAL',
    description: 'Use the MINI MURAL size guide to measure your nails, choose sizes for each finger and prepare the fit of your custom press-on nail set.',
  },
  '/about': {
    title: 'About MINI MURAL | Custom Press-On Nails',
    description: 'Meet MINI MURAL and explore our approach to personal nail design, from colors and shapes to the small details that make a set your own.',
  },
  '/contact': {
    title: 'Contact MINI MURAL | Nail Design Help',
    description: 'Contact MINI MURAL for help with nail designs, sizing and orders, or discuss the details of your custom press-on nail set.',
  },
  '/faq': {
    title: 'Custom Press-On Nails FAQ | MINI MURAL',
    description: 'Find answers to common questions about MINI MURAL nail designs, sizing, ordering and caring for your press-on nails.',
  },
  '/policies/privacy': {
    title: 'Privacy & Data Information | MINI MURAL',
    description: 'Read MINI MURAL information about personal data, locally saved designs and the information used when you place an order.',
  },
  '/policies/shipping': {
    title: 'Production & Shipping Information | MINI MURAL',
    description: 'Read the current MINI MURAL guidance on production, delivery and the shipping details to confirm with your order.',
  },
  '/policies/returns': {
    title: 'After-Sales & Design Changes | MINI MURAL',
    description: 'Read the current MINI MURAL guidance on custom-order changes, after-sales support and how to discuss an issue with your nail set.',
  },
  '/policies/terms': {
    title: 'Custom Nail Service Terms | MINI MURAL',
    description: 'Read MINI MURAL custom nail service information, including design confirmation, order details and customer responsibilities.',
  },
};

// Deployment reads this list too: only these exact paths without a query are indexable.
export const INDEXABLE_PATHS = Object.freeze(Object.keys(PUBLIC_PAGES));
const PRIVATE_QUERY_KEYS = ['design', 'article', 'cms_preview'];

function websiteStructuredData() {
  return {
    '@context': 'https://schema.org',
    '@graph': [
      { '@type': 'Organization', '@id': `${ORIGIN}/#organization`, name: BRAND, url: `${ORIGIN}/` },
      { '@type': 'WebSite', '@id': `${ORIGIN}/#website`, name: BRAND, url: `${ORIGIN}/`, publisher: { '@id': `${ORIGIN}/#organization` } },
    ],
  };
}

export function getSeoMetadata(input, { title } = {}) {
  const url = new URL(input, ORIGIN);
  const normalizedPath = url.pathname.replace(/\/+$/, '') || '/';
  const canonicalPath = normalizedPath === '/policies/refund' ? '/policies/returns' : normalizedPath;
  const publicPage = Object.hasOwn(PUBLIC_PAGES, canonicalPath) ? PUBLIC_PAGES[canonicalPath] : null;
  const privateQuery = PRIVATE_QUERY_KEYS.some(key => url.searchParams.has(key));
  const hasQuery = url.href.split('#', 1)[0].includes('?');
  const indexable = Object.hasOwn(PUBLIC_PAGES, url.pathname) && !hasQuery;
  return {
    title: title || publicPage?.title || 'MINI MURAL | Nail Design Studio',
    description: publicPage?.description || 'MINI MURAL nail design studio and customer services.',
    robots: indexable ? 'index,follow,max-image-preview:large' : 'noindex,follow',
    canonical: publicPage && !privateQuery ? `${ORIGIN}${canonicalPath}` : null,
    siteName: BRAND,
    structuredData: indexable ? websiteStructuredData() : null,
  };
}

function headElement(document, selector, tag, attributes, value) {
  const existing = [...document.head.querySelectorAll(selector)];
  let element = existing.shift();
  for (const duplicate of existing) duplicate.remove();
  if (value === null) { element?.remove(); return null; }
  if (!element) {
    element = document.createElement(tag);
    for (const [key, attribute] of Object.entries(attributes)) element.setAttribute(key, attribute);
    document.head.append(element);
  }
  return element;
}

export function applySeoMetadata(document, metadata) {
  if (document.title !== metadata.title) document.title = metadata.title;
  const setMeta = (attribute, key, value) => {
    const element = headElement(document, `meta[${attribute}="${key}"]`, 'meta', { [attribute]: key }, value);
    if (element && element.getAttribute('content') !== value) element.setAttribute('content', value);
  };
  setMeta('name', 'description', metadata.description);
  setMeta('name', 'robots', metadata.robots);
  setMeta('property', 'og:site_name', metadata.siteName);
  setMeta('property', 'og:type', 'website');
  setMeta('property', 'og:title', metadata.title);
  setMeta('property', 'og:description', metadata.description);
  setMeta('property', 'og:url', metadata.canonical);
  const canonical = headElement(document, 'link[rel="canonical"]', 'link', { rel: 'canonical' }, metadata.canonical);
  if (canonical && canonical.getAttribute('href') !== metadata.canonical) canonical.setAttribute('href', metadata.canonical);
  const structuredData = headElement(document, '#minimural-structured-data', 'script', { id: 'minimural-structured-data', type: 'application/ld+json' }, metadata.structuredData);
  const json = metadata.structuredData && JSON.stringify(metadata.structuredData).replace(/</g, '\\u003c');
  if (structuredData && structuredData.textContent !== json) structuredData.textContent = json;
}
